import React, { useState, useMemo } from 'react';
import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import Utils from '@/common/utils/utils';
import {
    canTicketHasStatus,
    changeTicketStatus,
    getCurrentStatusData,
    getCurrentTelephoneStatusData,
    onClickEditTicketStatus
} from './handler/TicketListHandler';
import { useSetRecoilState } from 'recoil';
import { Popover } from '@material-ui/core';
import { triggerReloadTab } from '@/state/selector/panelSelector';
import DataChangePleaseReloadModal from '../common/modal/DataChangePleaseReloadModal';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import './DetailTicketListStatus.css';

export default function DetailTicketListStatus(props) {
    // Popover change ticket status
    const [ticketStatusListPopover, setTicketStatusListPopover] = useState(null);
    const isOpenTicketStatusListPopover = Boolean(ticketStatusListPopover);
    const idTicketStatusListPopover = isOpenTicketStatusListPopover ? 'change-ticket-status' : '';
    const [openTicketStatusListPopoverId, setOpenTicketStatusListPopoverId] = useState(null);
    const reloadTabByPanelTypeAndTabId = useSetRecoilState(triggerReloadTab);
    const [isOpenModalReloadPage, setIsOpenModalReloadPage] = useState(false);
    // Subscribe to translation updates so displayed status names re-render on language change
    useTranslationSubscription();

    const {
        isHelpdeskListTicket,
        isCustomerListTicket,
        item,
        selectedTicket,
        isDeletedCorporationOrigin,
    } = {...props};

    const newProps = {
        ...props,
        setTicketStatusListPopover,
        setOpenTicketStatusListPopoverId
    };

    const isDraft = item.public === Constant.TICKET_PUBLIC.DRAFT

    // Status data
    let canHasStatus = canTicketHasStatus(item, isHelpdeskListTicket);
    let currentStatusData = getCurrentStatusData(item);
    let currentStatusItemData = currentStatusData?.currentStatusItemData;
    let statusItemList = currentStatusData?.statusItemList;
    let currentStatusItemStructure = Utils.convertJSONDataToObject(
        currentStatusItemData?.structure
    );
    let itemColor = item?.statusDetail
        ? Utils.convertJSONDataToObject(item?.statusDetail?.structure)?.color
        : '';
    let itemBackground = item?.statusDetail
        ? Utils.convertJSONDataToObject(item?.statusDetail?.structure)?.background
        : '';

    if (isHelpdeskListTicket && currentStatusItemStructure) {
        itemColor = currentStatusItemStructure?.helpdesk?.color || '';
        itemBackground = currentStatusItemStructure?.helpdesk?.background || '';
    }

    const ticketInvitationB2BStatus = useMemo(() => {
        let status = '';
        let classNameStatus = '';
        if (
            [
                Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK,
                Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER
            ].includes(item.ticketType) &&
            item.public !== Constant.TICKET_PUBLIC.DRAFT
        ) {
            switch (item.approveFlag) {
                case Constant.APPROVE_FLAG.INPROCESS:
                    status = Constant.B2B_INVITATION_STATUS.WAITING;
                    classNameStatus = 'watting';
                    break;
                case Constant.APPROVE_FLAG.APPROVE:
                    status = Constant.B2B_INVITATION_STATUS.APPROVED;
                    classNameStatus = 'approved';
                    break;
                case Constant.APPROVE_FLAG.REJECTED:
                    status = Constant.B2B_INVITATION_STATUS.REJECTED;
                    classNameStatus = 'rejected';
                    break;
                default:
                    break;
            }
        }
        return { status, classNameStatus };
    }, [item]);

    const statusTicketInvitationGuestOrGuestDesk = useMemo(() => {
        let status = '';
        let classNameStatus = '';
        if (
            [
                Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT,
                Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT
            ].includes(item.ticketType)
        ) {
            let statusTicket = item.invitationFlag;
            switch (statusTicket) {
                case Constant.INVITATION_FLAG.INPROCESS:
                    status = Constant.B2B_INVITATION_STATUS.WAITING;
                    classNameStatus = 'watting';
                    break;
                case Constant.INVITATION_FLAG.INVITE_SUCCESS:
                    status = Constant.B2B_INVITATION_STATUS.APPROVED;
                    classNameStatus = 'approved';
                    break;
                case Constant.INVITATION_FLAG.INVITE_REJECTED:
                    status = Constant.B2B_INVITATION_STATUS.REJECTED;
                    classNameStatus = 'rejected';
                    break;
                default:
                    break;
            }
        }
        return { status, classNameStatus };
    }, [item]);

    /**
     * Map telephone call status value to label and CSS class
     * @param {string} callStatus - Call status value
     * @returns {object} - Object with status label and classNameStatus
     */
    const mapTelephoneCallStatusToLabel = (callStatus) => {
        const statusMap = {
            [TelephoneConstant.TELEPHONE_CALL_STATUS.CALL_WAITING]: {
                status: TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.CALL_WAITING,
                classNameStatus: 'telephone-call-waiting'
            },
            [TelephoneConstant.TELEPHONE_CALL_STATUS.DURING_CALL]: {
                status: TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.DURING_CALL,
                classNameStatus: 'telephone-during-call'
            },
            [TelephoneConstant.TELEPHONE_CALL_STATUS.END_CALL]: {
                status: TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.END_CALL,
                classNameStatus: 'telephone-end-call'
            },
            [TelephoneConstant.TELEPHONE_CALL_STATUS.MISSING_CALL]: {
                status: TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.MISSING_CALL,
                classNameStatus: 'telephone-missing-call'
            },
            [TelephoneConstant.TELEPHONE_CALL_STATUS.CANCELLED_CALL]: {
                status: TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.CANCELLED_CALL,
                classNameStatus: 'telephone-cancelled-call'
            }
        };

        return statusMap[callStatus] || { status: '', classNameStatus: '' };
    };

    const currentTelephoneStatusData = useMemo(() => {
        return getCurrentTelephoneStatusData(item);
    }, [item]);

    // Telephone ticket status
    const telephoneTicketStatus = useMemo(() => {
        if (item.ticketType !== Constant.TICKET_TYPE.TELEPHONE) {
            return { status: '', classNameStatus: '' };
        }

        const callStatus = currentTelephoneStatusData?.currentStatusField || TelephoneConstant.TELEPHONE_CALL_STATUS.END_CALL;

        // Map status value to label and styling
        return mapTelephoneCallStatusToLabel(callStatus);
    }, [item?.ticketType, currentTelephoneStatusData?.currentStatusField]);

    const renderStatusItemList = (itemList) => {
        let itemListConvert = [];
        if (!Utils.isEmptyArray(itemList)) {
            itemList.forEach(itemStatus => {
                let itemStructure = Utils.convertJSONDataToObject(itemStatus.structure);
                if (itemStructure.no !== Constant.STATUS_NO.WAITING) {
                    let itemData = {
                        id: itemStatus.id,
                        name: tr(Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, itemStatus.name)),
                        no: itemStructure.no,
                        color: itemStructure.helpdesk?.color,
                        background: itemStructure.helpdesk?.background
                    };
                    itemListConvert.push(itemData);
                }
            });

            itemListConvert = itemListConvert.sort((a, b) => a.no - b.no);
        }
        return (
            <div className="status-item-list-content">
                {itemListConvert.map(itemStatus => (
                    <div
                        className={
                            itemStatus.id === selectedTicket.statusDetail?.currentStatus
                                ? 'status-item-wrap active'
                                : 'status-item-wrap'
                        }
                        key={`status-item-content-${itemStatus.id}`}
                    >
                        <div
                            className="status-item-content"
                            key={`${itemStatus.id}`}
                            style={{
                                color: `${itemStatus.color}`,
                                background: `${itemStatus.background}`
                            }}
                            onClick={e => changeTicketStatus(itemStatus.id, newProps, itemStatus.no, reloadTabByPanelTypeAndTabId, setIsOpenModalReloadPage)}
                        >
                            {itemStatus.name}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const getCursorClassForStatus = () => {
        if (Utils.isDisableEditEmailTicket(item, props?.projectInuseDetail, props?.corporationDetail, currentStatusItemStructure?.no)) {
            return 'context-menu';
        }

        if (
            Utils.isEmptyString(item.deletedAt) &&
            currentStatusItemStructure.no !== Constant.STATUS_NO.WAITING &&
            !isDeletedCorporationOrigin &&
            !Utils.isAnonymousQAEndLiveChatTicket(item)
        ) {
            return 'pointer';
        }

        return 'context-menu';
    };

    return (
        <>
            {isCustomerListTicket &&
                canHasStatus && item.ticketType !== Constant.TICKET_TYPE.TELEPHONE &&
                item?.statusDetail &&
                item?.statusDetail?.name && (
                    <span
                        style={{
                            color: `${itemColor}`,
                            background: `${itemBackground}`,
                            cursor: 'context-menu'
                        }}
                    >
                        {tr(Utils.getTextByLanguage(
                            Constant.LANGUAGE_TYPE.JP,
                            item?.statusDetail?.name
                        ))}
                    </span>
                )}
            {isHelpdeskListTicket &&
                canHasStatus && item.ticketType !== Constant.TICKET_TYPE.TELEPHONE &&
                currentStatusItemData &&
                currentStatusItemData?.name &&
                currentStatusItemStructure &&
                !Utils.isEmptyArray(statusItemList) && (
                    <>
                        <span
                            style={{
                                color: `${itemColor}`,
                                background: `${itemBackground}`,
                                cursor: getCursorClassForStatus()
                            }}
                            aria-describedby={idTicketStatusListPopover}
                            id={idTicketStatusListPopover}
                            onClick={event => {
                                onClickEditTicketStatus(
                                    event,
                                    item,
                                    currentStatusItemStructure,
                                    newProps,
                                    isDeletedCorporationOrigin
                                );
                            }}
                        >
                            {tr(Utils.getTextByLanguage(
                                Constant.LANGUAGE_TYPE.JP,
                                currentStatusItemData.name
                            ))}
                        </span>

                        {statusItemList && !Utils.isEmptyArray(statusItemList) && (
                            <Popover
                                id={`change-ticket-status-${item.id}`}
                                key={`change-ticket-status-${item.id}`}
                                className="select-status-content-wrap"
                                open={
                                    isOpenTicketStatusListPopover &&
                                    openTicketStatusListPopoverId === item.id &&
                                    Utils.isEmptyString(item.deletedAt)
                                }
                                anchorEl={ticketStatusListPopover}
                                onClose={e => {
                                    setTicketStatusListPopover(null);
                                    setOpenTicketStatusListPopoverId(null);
                                }}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                }}
                            >
                                <div className="ticket-status-list">
                                    {renderStatusItemList(statusItemList)}
                                </div>
                            </Popover>
                        )}
                    </>
                )}

            {[
                Constant.TICKET_TYPE.REQUEST_EXTERNAL_DESK,
                Constant.TICKET_TYPE.REQUEST_EXTERNAL_CUSTOMER
            ].includes(item.ticketType) &&
            ticketInvitationB2BStatus?.status && !isDraft && (
                    <span
                        className={`invitation-status ${ticketInvitationB2BStatus?.classNameStatus}`}
                    >
                        {tr(ticketInvitationB2BStatus?.status)}
                    </span>
                )}

            {[
                Constant.TICKET_TYPE.INVITATION_GUEST_PROJECT,
                Constant.TICKET_TYPE.INVITATION_GUEST_DESK_PROJECT
            ].includes(item.ticketType) && !isDraft &&
            statusTicketInvitationGuestOrGuestDesk?.status && (
                    <span
                        className={`invitation-status ${statusTicketInvitationGuestOrGuestDesk?.classNameStatus}`}
                    >
                        {tr(statusTicketInvitationGuestOrGuestDesk?.status)}
                    </span>
                )}

            {/* Telephone ticket status */}
            {item.ticketType === Constant.TICKET_TYPE.TELEPHONE && 
             telephoneTicketStatus?.status && (
                <span
                    className={`telephone-status ${telephoneTicketStatus?.classNameStatus}`}
                >
                    {tr(telephoneTicketStatus.status)}
                </span>
            )}

            <DataChangePleaseReloadModal
                isOpen={isOpenModalReloadPage}
                setIsOpen={setIsOpenModalReloadPage}
            />
        </>
    );
}
